import { useState, type FormEvent } from 'react';
import { Tablet, Link2 } from 'lucide-react';
import { pairBanepadDevice } from '../../lib/banepadDevices';
import type { BanepadDeviceRegistration, PairError } from '../../engine/banepadDevice';

export interface BanepadDeviceRegisterProps {
  /** Called once the entered code pairs successfully (carries the paired registration + token). */
  onPaired: (registration: BanepadDeviceRegistration) => void;
}

/**
 * How long a pairing code is, in characters the operator types.
 *
 * NO PREFIX (#216). Codes used to read «BANE-7K3Q»: four random characters
 * behind a constant word. The word carried no information — every code had it
 * — so it cost four characters of typing and bought nothing, while the random
 * part was only 32⁴ ≈ 1 million. Dropping it and spending the same keystrokes
 * on randomness gives 32⁸ ≈ 10¹² for the same effort, and matches what the
 * tenant migration already issues.
 *
 * Product decision by the owner, 2026-09-20: "it is more secure to remove the
 * bane part".
 */
export const REGISTRATION_CODE_LENGTH = 8;

/**
 * Pure: turn whatever was typed or pasted into the canonical stored form.
 * Upper-cases (tablet keyboards default to lower-case), drops anything that is
 * not a letter or digit (spaces, dashes, stray punctuation), and caps the
 * length. This is the same normalisation the server applies to the code it
 * compares — see normalisePairingCode — so what is typed here and what is
 * stored cannot disagree about spelling.
 */
export function formatRegistrationCodeInput(raw: string): string {
  const typed = String(raw ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  // A LEGACY «BANE-XXXX» IS STILL ACCEPTED, because codes issued before this
  // change are still sitting in clubs' registries. `BANE7K3Q` is exactly 8
  // characters, so it passes through untouched and matches what is stored —
  // stripping the word here would break those.
  return typed.slice(0, REGISTRATION_CODE_LENGTH);
}

/** Pure: true once the whole code has been typed and can be submitted. */
export function isCompleteRegistrationCode(code: string): boolean {
  return code.length === REGISTRATION_CODE_LENGTH;
}

function pairErrorText(error: PairError): string {
  if (error === 'revoked') return 'Denne koden er tilbakekalt. Be Sentralen lage en ny.';
  if (error === 'expired') return 'Koden er utløpt. Be Sentralen om en ny kode.';
  // NOT REACHABLE ON A DEPLOYED PAD (#663). It means this build has no API to
  // pair against, and nothing is allowed to mint a credential in its place — a
  // developer's configuration problem, so it says so rather than blaming the code
  // the operator just typed correctly.
  if (error === 'server_required') {
    return 'Denne versjonen er ikke koblet til Starpoint-serveren, så paring er ikke mulig.';
  }
  return 'Ugyldig kode. Sjekk at koden er skrevet riktig, og prøv igjen.';
}

/**
 * Physical-pad registration screen — the operator enters the registration code
 * generated in the Banepad central/rutenett view to bind THIS tablet to a court.
 * Pairs via the existing persisted boundary; the assigned court is decided by the
 * returned token, never by anything in the URL.
 */
export function BanepadDeviceRegister({ onPaired }: BanepadDeviceRegisterProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (!isCompleteRegistrationCode(code) || busy) return;
    setError(null);
    setBusy(true);
    try {
      const res = await pairBanepadDevice(code);
      // `in` narrowing: the repo's non-strict TS does not reliably narrow a
      // boolean-literal discriminant, but narrows on a unique property.
      if ('error' in res) {
        setError(pairErrorText(res.error));
        return;
      }
      onPaired(res.registration);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-bg-soft">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3 text-ink-2">
            <Tablet size={40} />
          </div>
          <h1 className="font-display text-2xl font-bold text-ink">Koble BanePad</h1>
        </div>

        <div className="card-elev p-6">
          <p className="text-ink-2 mb-4" style={{ fontSize: 15 }}>
            Skriv inn koden fra Sentralen for å koble denne paden til en bane.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label
                htmlFor="banepad-device-code"
                className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5"
              >
                Registreringskode
              </label>
              <input
                id="banepad-device-code"
                value={code}
                onChange={(e) => {
                  setCode(formatRegistrationCodeInput(e.target.value));
                  setError(null);
                }}
                // A DELIBERATE autoFocus (#900). This is a court tablet's
                // pairing screen: its one heading, one sentence and this one
                // field are the whole page, and the code is all it can do. The
                // person at the pad has just been handed the code by the desk,
                // and focusing the field is what brings the tablet's keyboard
                // up. No content is skipped by it, so it stays.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                inputMode="text"
                enterKeyHint="go"
                // NO `maxLength` (#815). A browser truncates a paste to fit
                // `maxlength` BEFORE `onChange` runs, so a legacy «BANE-7K3Q»
                // (nine characters) arrived as «BANE-7K3», normalised to seven
                // and could never be submitted. `formatRegistrationCodeInput`
                // caps the length itself, AFTER separators are gone, so it is
                // the only cap — typing past eight still stops at eight.
                spellCheck={false}
                placeholder="XXXXXXXX"
                aria-describedby="banepad-device-code-hint"
                className="input w-full font-mono uppercase tracking-widest"
                style={{ fontSize: 26, padding: '0.85rem 1rem', textAlign: 'center' }}
              />
              <p id="banepad-device-code-hint" className="text-[11px] text-ink-3 mt-1.5">
                Skriv inn koden fra Sentralen — store bokstaver settes inn automatisk. Koden
                inneholder aldri O, 0, I eller 1.
              </p>
            </div>

            {error && (
              <div className="text-sm text-rose font-medium px-3 py-2 rounded bg-rose/10 border border-rose/20">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !isCompleteRegistrationCode(code)}
              className="btn-lime w-full justify-center"
              style={{ padding: '0.85rem 1.125rem', fontSize: 16 }}
            >
              <Link2 size={18} /> {busy ? 'Kobler…' : 'Koble paden'}
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] text-ink-3 mt-5">
          Enhetskobling — ikke innlogging. Paden får kun tilgang til sin egen bane.
        </p>
      </div>
    </div>
  );
}
